import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabaseClient";
import { Download, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { z } from "zod";

const emailCaptureSchema = z.object({
  email: z.string()
    .trim()
    .email("Invalid email address")
    .max(255, "Email must be less than 255 characters")
    .refine((val) => {
      const disposableDomains = ['tempmail.com', 'throwaway.email', '10minutemail.com', 'guerrillamail.com'];
      const domain = val.split('@')[1]?.toLowerCase();
      return !disposableDomains.includes(domain);
    }, "Disposable email addresses are not allowed"),
  name: z.string()
    .trim()
    .max(100, "Name must be less than 100 characters")
    .optional(),
  consent: z.boolean().refine((val) => val === true, {
    message: "You must consent to data processing",
  }),
});

interface EmailCaptureFormProps {
  resourceName: string;
  resourceType: string;
  buttonText?: string;
  onSuccess?: (email: string) => void;
  showNameField?: boolean;
  resourceFilename?: string;
}

export const EmailCaptureForm = ({
  resourceName,
  resourceType,
  buttonText = "Download Now",
  onSuccess,
  showNameField = false,
  resourceFilename,
}: EmailCaptureFormProps) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    try {
      emailCaptureSchema.parse({
        email,
        name: showNameField ? name : undefined,
        consent,
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Save to resource_downloads table
      const { error: downloadError } = await supabase
        .from("resource_downloads")
        .insert({
          email,
          resource_name: resourceName,
          resource_type: resourceType,
        });

      if (downloadError) throw downloadError;

      // Increment download count (failsafe in addition to database trigger)
      await supabase.rpc('increment_resource_count', {
        resource_title: resourceName
      });

      // Save to email_subscribers table (ignore duplicates)
      const { error: subscriberError } = await supabase
        .from("email_subscribers")
        .insert({
          email,
          name: showNameField ? name : null,
          subscription_source: `resource_download_${resourceType}`,
        });

      if (subscriberError && subscriberError.code !== "23505") {
        // Ignore duplicate key errors (user already subscribed)
        throw subscriberError;
      }

      // Save to leads table (ignore duplicates)
      const { error: leadError } = await supabase
        .from("leads")
        .insert({
          email,
          name: showNameField ? name : null,
          lead_source: "resource_download",
          lead_type: resourceType,
          notes: `Downloaded: ${resourceName}`,
        });

      if (leadError && leadError.code !== "23505") {
        throw leadError;
      }

      // Generate signed URL for the resource
      let downloadUrl: string;
      
      if (resourceFilename) {
        // New system: use filename from database and generate signed URL
        const { data: signedUrlData, error: signedUrlError } = await supabase.storage
          .from('resources')
          .createSignedUrl(resourceFilename, 3600); // 1 hour expiry

        if (signedUrlError) throw signedUrlError;
        downloadUrl = signedUrlData?.signedUrl || '';
      } else {
        // Legacy system: use hardcoded map (for backwards compatibility)
        const resourceFileMap: Record<string, string> = {
          "The Ultimate SME Cyber Security Checklist": "sme-checklist.pdf",
          "Incident Response Plan Template": "incident-response-template.pdf",
          "Comprehensive Risk Assessment Guide": "risk-assessment-guide.pdf",
          "UK Cyber Security Compliance Framework": "compliance-framework.pdf",
          "Information Security Policy Template": "security-policy-template.pdf",
          "Cyber Insurance Buyer's Guide": "cyber-insurance-guide.pdf",
        };

        const fileName = resourceFileMap[resourceName] || "resource.pdf";
        
        // Generate signed URL for legacy resources too
        const { data: signedUrlData, error: signedUrlError } = await supabase.storage
          .from('resources')
          .createSignedUrl(fileName, 3600);

        if (signedUrlError) throw signedUrlError;
        downloadUrl = signedUrlData?.signedUrl || '';
      }

      // Send email via edge function
      const { error: emailError } = await supabase.functions.invoke("send-resource-email", {
        body: {
          email,
          resourceName,
          resourceType,
          downloadUrl,
        },
      });

      if (emailError) {
        console.error("Error sending email:", emailError);
        // Don't throw - data is saved, just email failed
        toast({
          title: "Partial Success",
          description: "Your download request was saved, but there was an issue sending the email. Please contact us.",
          variant: "destructive",
        });
        return;
      }

      console.log("Form submitted and email sent successfully");
      
      toast({
        title: "Success!",
        description: "Check your email for the download link!",
      });

      // Reset form
      setEmail("");
      setName("");
      setConsent(false);
      
      // Call success callback
      if (onSuccess) {
        onSuccess(email);
      }
    } catch (error: any) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {showNameField && (
        <div>
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
          />
        </div>
      )}
      <div>
        <Label htmlFor="email">Email Address *</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          required
        />
      </div>
      <div className="flex items-start space-x-2">
        <Checkbox 
          id="resource-consent" 
          required 
          checked={consent}
          onCheckedChange={(checked) => setConsent(checked === true)}
        />
        <Label htmlFor="resource-consent" className="text-sm leading-relaxed cursor-pointer">
          I consent to 4FOX storing my information and sending me this resource. 
          See our <Link to="/privacy-policy" className="underline hover:text-primary">Privacy Policy</Link>.
        </Label>
      </div>
      <Button type="submit" className="w-full" disabled={isSubmitting || !consent}>
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <Download className="mr-2 h-4 w-4" />
            {buttonText}
          </>
        )}
      </Button>
    </form>
  );
};
